import { useEffect, useState } from "react";
import { API_BASE } from "../api";

// The picture behind a proposition. The API draws the asset's VWAP story
// chart (ribbon, structural levels, key zone, volume vs normal, cost-basis
// curve) with the proposal's or strike's entry, stop and target on it. The
// image is fetched with credentials (the API lives on another origin) and
// shown in an overlay, so a table row stays a table row until asked.

type Props = { symbol: string; entry?: number | null; stop?: number | null; target?: number | null; side?: string; label?: string };

export function storyChartPath(p: Props): string {
  const q = new URLSearchParams();
  if (p.entry != null) q.set("entry", String(p.entry));
  if (p.stop != null) q.set("stop", String(p.stop));
  if (p.target != null) q.set("target", String(p.target));
  if (p.side) q.set("side", p.side);
  if (p.label) q.set("label", p.label);
  return `/api/intel/story/${encodeURIComponent(p.symbol)}.png?${q.toString()}`;
}

export function ChartImg({ path, alt }: { path: string; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    setSrc(null); setErr(null);
    fetch(`${API_BASE}${path}`, { credentials: "include" })
      .then(async r => { if (!r.ok) throw new Error(`${r.status}`); return r.blob(); })
      .then(b => { if (!alive) return; url = URL.createObjectURL(b); setSrc(url); })
      .catch(e => alive && setErr(String(e.message || e)));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (err) return <div className="text-xs text-down">chart unavailable ({err})</div>;
  if (!src) return <div className="text-xs text-slate-500 py-6 text-center">drawing the chart…</div>;
  return <img src={src} alt={alt} className="w-full rounded-lg border border-edge bg-white" />;
}

export function StoryChartButton(p: Props & { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={`text-xs text-accent hover:underline ${p.className ?? ""}`} onClick={e => { e.stopPropagation(); setOpen(true); }} title="the VWAP story chart with this proposition's levels">chart</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-5xl card space-y-2" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{p.symbol} {p.side ?? ""} {p.label ?? ""} · where price sits against participant cost basis</h3>
              <button className="btn" onClick={() => setOpen(false)}>close</button>
            </div>
            <ChartImg path={storyChartPath(p)} alt={`${p.symbol} story chart`} />
          </div>
        </div>
      )}
    </>
  );
}
